import { useEffect, useRef, useState } from 'react';
import { Box, Button, Container, LinearProgress, Paper, Radio, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { driverNeedsKnowledgeTraining, useAuth } from '../context/AuthContext';
import {
  KnowledgeQuizQuestion,
  KNOWLEDGE_QUIZ_PASS_PERCENT,
  buildKnowledgeQuiz,
  clearKnowledgeQuizLock,
  orderQuizForAttempt,
  setKnowledgeQuizLock,
} from '../knowledgeQuiz';

export default function DriverQuiz() {
  const { currentUser, knowledgeBase, quizQuestions, quizBankReady, submitKnowledgeQuiz, setKnowledgeQuizActive } = useAuth();
  const navigate = useNavigate();
  const [started, setStarted] = useState(false);
  const [questions, setQuestions] = useState<KnowledgeQuizQuestion[]>([]);
  const [index, setIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<{ score: number; passed: boolean } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const startedRef = useRef(false);
  const finishedRef = useRef(false);
  const submittedRef = useRef(false);
  const startedAtRef = useRef(0);

  const readIds = new Set(currentUser?.knowledgeTopicsRead || []);
  const allRead =
    knowledgeBase.length > 0 && knowledgeBase.every((article) => readIds.has(article.id));
  const needsTraining = driverNeedsKnowledgeTraining(currentUser);

  useEffect(() => {
    if (!currentUser) {
      navigate('/login', { replace: true });
      return;
    }
    if (!needsTraining && !startedRef.current && currentUser.knowledgeQuizScore == null) {
      navigate('/schedule', { replace: true });
      return;
    }
    if (needsTraining && !allRead && !startedRef.current) {
      navigate('/training', { replace: true });
    }
  }, [allRead, currentUser, navigate, needsTraining]);

  useEffect(() => {
    if (!started) return undefined;

    const voidAttempt = () => {
      if (finishedRef.current) return;
      finishedRef.current = true;
      clearKnowledgeQuizLock();
      sessionStorage.setItem('sds_quiz_voided', '1');
      setKnowledgeQuizActive(false);
      if (document.fullscreenElement) {
        void document.exitFullscreen?.().catch(() => {});
      }
      navigate('/training', { replace: true, state: { quizVoided: true } });
    };

    const onVisibility = () => {
      if (document.hidden) voidAttempt();
    };
    const onBlur = () => {
      if (finishedRef.current || submittedRef.current) return;
      if (Date.now() - startedAtRef.current < 800) return;
      voidAttempt();
    };
    const onPageHide = () => voidAttempt();

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('blur', onBlur);
    window.addEventListener('pagehide', onPageHide);

    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('blur', onBlur);
      window.removeEventListener('pagehide', onPageHide);
      if (!finishedRef.current && !submittedRef.current) {
        finishedRef.current = true;
        sessionStorage.setItem('sds_quiz_voided', '1');
        clearKnowledgeQuizLock();
        setKnowledgeQuizActive(false);
      }
    };
  }, [navigate, setKnowledgeQuizActive, started]);

  const beginQuiz = async () => {
    const nextQuestions = quizBankReady ? orderQuizForAttempt(quizQuestions) : buildKnowledgeQuiz(knowledgeBase);
    if (nextQuestions.length === 0 || !currentUser) {
      setError(
        quizBankReady
          ? 'An administrator has not added quiz questions yet.'
          : 'The knowledge base does not have enough material for a quiz yet.'
      );
      return;
    }
    startedAtRef.current = Date.now();
    startedRef.current = true;
    finishedRef.current = false;
    setKnowledgeQuizLock(currentUser.id);
    setKnowledgeQuizActive(true);
    setQuestions(nextQuestions);
    setAnswers({});
    setIndex(0);
    setStarted(true);
    try {
      await document.documentElement.requestFullscreen();
    } catch {
      // Fullscreen can be blocked. Leaving the window still ends the attempt.
    }
  };

  const question = questions[index];
  const selected = question ? answers[question.id] : undefined;

  const submitQuiz = async () => {
    if (submitting || finishedRef.current) return;
    const correctCount = questions.filter((item) => answers[item.id] === item.answer).length;
    const score = Math.round((correctCount / questions.length) * 100);
    const passed = score >= KNOWLEDGE_QUIZ_PASS_PERCENT;
    submittedRef.current = true;
    setResult({ score, passed });
    setError(null);
    setSubmitting(true);
    const res = await submitKnowledgeQuiz(correctCount, questions.length);
    setSubmitting(false);
    if (!res.success || res.score == null || res.passed == null) {
      submittedRef.current = false;
      setError(res.error || 'Could not save your quiz result.');
      return;
    }
    finishedRef.current = true;
    setResult({ score: res.score, passed: res.passed });
    clearKnowledgeQuizLock();
    setKnowledgeQuizActive(false);
    if (document.fullscreenElement) {
      void document.exitFullscreen?.().catch(() => {});
    }
  };

  const retakeQuiz = () => {
    finishedRef.current = false;
    submittedRef.current = false;
    setResult(null);
    setError(null);
    void beginQuiz();
  };

  const retakeWasRequired = needsTraining && result?.passed === true;
  const displayResult = retakeWasRequired
    ? null
    : result ??
      (!started && currentUser?.knowledgeQuizScore != null
        ? {
            score: currentUser.knowledgeQuizScore,
            passed: currentUser.knowledgeQuizPassed === true,
          }
        : null);

  if (!displayResult && (!needsTraining || !allRead)) return null;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'grey.50',
        paddingTop: { xs: '84px', sm: '230px' },
        pb: 6,
        userSelect: 'none',
      }}
      onCopy={(event) => event.preventDefault()}
      onContextMenu={(event) => event.preventDefault()}
    >
      <Container maxWidth="md">
        <Paper sx={{ p: { xs: 2.5, sm: 4 }, borderRadius: 3 }}>
          {(!started || retakeWasRequired) && !displayResult && (
            <>
              <Typography variant="h4" fontWeight={800} gutterBottom>
                Knowledge Base Quiz
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                This quiz covers every topic you just read. You need {KNOWLEDGE_QUIZ_PASS_PERCENT}% to
                pass. If you do not pass, you can study and take it again.
              </Typography>
              <Typography variant="body1" sx={{ mb: 3 }}>
                Stay on this screen until you submit. Switching windows, opening another tab, or
                leaving this page ends the attempt. The knowledge base stays locked while the quiz is
                open, including in another window.
              </Typography>
              {error && (
                <Typography color="error" sx={{ mb: 2 }}>
                  {error}
                </Typography>
              )}
              <Button variant="contained" size="large" onClick={beginQuiz}>
                Begin quiz
              </Button>
            </>
          )}

          {started && !retakeWasRequired && question && !displayResult && (
            <>
              <Typography variant="overline" color="text.secondary">
                Question {index + 1} of {questions.length}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={(index / questions.length) * 100}
                sx={{ my: 1.5, borderRadius: 1 }}
              />
              <Typography variant="caption" color="primary" fontWeight={700}>
                {question.topic}
              </Typography>
              <Typography variant="h6" fontWeight={700} sx={{ mt: 0.5, mb: 2 }}>
                {question.prompt}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                {question.choices.map((choice) => {
                  const chosen = selected === choice;
                  return (
                    <Box
                      key={choice}
                      onClick={() => setAnswers((prev) => ({ ...prev, [question.id]: choice }))}
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 1,
                        p: 1.5,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: chosen ? 'primary.main' : 'divider',
                        bgcolor: chosen ? 'rgba(25, 118, 210, 0.08)' : 'background.paper',
                        cursor: 'pointer',
                      }}
                    >
                      <Radio checked={chosen} sx={{ p: 0.25 }} />
                      <Typography variant="body2" sx={{ pt: 0.35 }}>
                        {choice}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
              {error && (
                <Typography color="error" sx={{ mt: 2 }}>
                  {error}
                </Typography>
              )}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
                <Button disabled={index === 0} onClick={() => setIndex((prev) => prev - 1)}>
                  Back
                </Button>
                {index < questions.length - 1 ? (
                  <Button
                    variant="contained"
                    disabled={!selected}
                    onClick={() => setIndex((prev) => prev + 1)}
                  >
                    Next
                  </Button>
                ) : (
                  <Button variant="contained" disabled={!selected || submitting} onClick={submitQuiz}>
                    {submitting ? 'Saving...' : 'Submit quiz'}
                  </Button>
                )}
              </Box>
            </>
          )}

          {displayResult && (
            <>
              <Typography variant="h4" fontWeight={800} gutterBottom>
                {displayResult.passed ? 'Passed Test' : 'Not passed yet'}
              </Typography>
              <Typography
                variant="h3"
                color={displayResult.passed ? 'success.main' : 'warning.main'}
                fontWeight={800}
              >
                {displayResult.score}%
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mt: 1, mb: 3 }}>
                {submitting
                  ? 'Saving your score...'
                  : displayResult.passed
                    ? `You passed. A score of ${KNOWLEDGE_QUIZ_PASS_PERCENT}% or higher is required.`
                    : `You need ${KNOWLEDGE_QUIZ_PASS_PERCENT}% to pass. You can retake the quiz now.`}
              </Typography>
              {error && (
                <Typography color="error" sx={{ mb: 2 }}>
                  {error}
                </Typography>
              )}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                {error ? (
                  <Button variant="contained" disabled={submitting} onClick={submitQuiz}>
                    Save score again
                  </Button>
                ) : displayResult.passed ? (
                  <>
                    <Button
                      variant="contained"
                      disabled={submitting}
                      onClick={() => navigate('/knowledge-base')}
                    >
                      Knowledge base
                    </Button>
                    <Button
                      variant="outlined"
                      disabled={submitting}
                      onClick={() => navigate('/schedule')}
                    >
                      My schedule
                    </Button>
                  </>
                ) : (
                  <Button variant="contained" disabled={submitting} onClick={retakeQuiz}>
                    Retake quiz
                  </Button>
                )}
              </Box>
            </>
          )}
        </Paper>
      </Container>
    </Box>
  );
}
